

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSocket } from '../ChatPage/socket';

const ACK_TIMEOUT = 15000;
const OPERATION_TTL = 130000;

/*
 * CatalogReview DOMAIN realtime only.
 *
 * В этом hook больше НЕТ cursor / hover / scroll / camera / presence UI /
 * field-lock визуализации. Универсальный интерфейс совместной работы целиком
 * принадлежит adminRealtimeUiBridge.
 *
 * Здесь остаются только данные каталога и операции, которые действительно
 * относятся к бизнес-логике CatalogReview: workspace, mutations, draftPatch,
 * operation и dataChanged.
 */

const emitWithAck = (socket, eventName, payload) => new Promise((resolve, reject) => {
    if (!socket?.connected) {
        const error = new Error('Нет соединения с сервером');
        error.code = 'CATALOG_REVIEW_SOCKET_OFFLINE';
        reject(error);
        return;
    }

    let settled = false;

    const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('Сервер не ответил вовремя'));
    }, ACK_TIMEOUT);

    socket.emit(eventName, payload, (result = {}) => {
        if (settled) return;

        settled = true;
        window.clearTimeout(timer);

        if (result.ok === false) {
            const error = new Error(
                result.error || 'Ошибка совместного редактирования'
            );
            error.code = result.code;
            error.latest = result.latest;
            reject(error);
            return;
        }

        resolve(result);
    });
});

const tokenPayload = () => ({
    token: window.localStorage.getItem('token'),
});

const mutationId = () => (
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const emitVolatile = (socket, eventName, payload) => {
    const transport = socket?.volatile || socket;
    transport?.emit?.(eventName, payload);
};

const revisionOf = (workspace) => Math.max(0, Number(workspace?.revision || 0));

const mergeWorkspacePatch = (left = {}, right = {}) => {
    const next = { ...left, ...right };

    [
        'oldFilters',
        'matchingDraft',
        'importDraft',
        'reviewModal',
        'pricingDraft',
        'compare',
    ].forEach((key) => {
        if (left[key] || right[key]) {
            next[key] = {
                ...(left[key] || {}),
                ...(right[key] || {}),
            };
        }
    });

    return next;
};

export const useCatalogReviewSocket = ({
    enabled = true,
    onOldUpdated,
    onItemUpdated,
    onProjectUpdated,
    onWorkspace,
    onDraftPatch,
    onDataChanged,
}) => {
    const [connected, setConnected] = useState(false);
    const [workspace, setWorkspace] = useState(null);
    const [operations, setOperations] = useState({});

    const handlersRef = useRef({});
    const workspaceRef = useRef(null);
    const workspaceRevisionRef = useRef(-1);
    const joinedRef = useRef(false);

    const operationTimersRef = useRef(new Map());
    const queuedOperationsRef = useRef(new Map());
    const operationSentAtRef = useRef(new Map());
    const operationStartedRef = useRef(new Set());

    const draftChannelsRef = useRef(new Map());
    const workspaceChannelRef = useRef({
        inFlight: false,
        pendingPatch: null,
        pendingWaiters: [],
        inFlightWaiters: [],
    });

    useEffect(() => {
        handlersRef.current = {
            onOldUpdated,
            onItemUpdated,
            onProjectUpdated,
            onWorkspace,
            onDraftPatch,
            onDataChanged,
        };
    }, [
        onOldUpdated,
        onItemUpdated,
        onProjectUpdated,
        onWorkspace,
        onDraftPatch,
        onDataChanged,
    ]);

    const applyWorkspace = useCallback((nextWorkspace, meta = {}) => {
        if (!nextWorkspace) return false;

        const nextRevision = revisionOf(nextWorkspace);
        const currentRevision = workspaceRevisionRef.current;

        if (
            currentRevision >= 0
            && nextRevision < currentRevision
            && meta.force !== true
        ) {
            return false;
        }

        workspaceRevisionRef.current = nextRevision;
        workspaceRef.current = nextWorkspace;
        setWorkspace(nextWorkspace);
        handlersRef.current.onWorkspace?.(nextWorkspace, meta);
        return true;
    }, []);

    const applyOperations = useCallback((items = []) => {
        const next = {};

        (Array.isArray(items) ? items : []).forEach((operation) => {
            if (!operation?.key || operation.running !== true) return;
            next[operation.key] = {
                ...operation,
                receivedAt: Date.now(),
            };
        });

        setOperations(next);
    }, []);

    const resyncState = useCallback(async () => {
        if (!enabled) return null;

        const socket = getSocket();
        if (!socket.connected || !joinedRef.current) return null;

        const result = await emitWithAck(socket, 'catalogReview:getState', {
            ...tokenPayload(),
        });

        if (Array.isArray(result.operations)) {
            applyOperations(result.operations);
        }

        applyWorkspace(result.workspace, {
            remote: true,
            authoritative: true,
            force: true,
        });

        return result;
    }, [applyOperations, applyWorkspace, enabled]);

    useEffect(() => {
        const socket = getSocket();

        if (!enabled) {
            joinedRef.current = false;
            setConnected(false);
            setWorkspace(null);
            workspaceRef.current = null;
            workspaceRevisionRef.current = -1;
            setOperations({});

            if (socket.connected) {
                socket.emit('catalogReview:leave', tokenPayload());
            }

            return undefined;
        }

        const join = () => {
            emitWithAck(socket, 'catalogReview:join', {
                ...tokenPayload(),
            }).then((result) => {
                joinedRef.current = true;
                setConnected(true);
                applyOperations(result.operations || []);

                applyWorkspace(result.workspace, {
                    initial: true,
                    remote: true,
                    authoritative: true,
                    force: true,
                    socketId: result.socketId || socket.id || '',
                });
            }).catch((error) => {
                joinedRef.current = false;
                setConnected(false);
                console.warn('catalogReview join error:', error.message);
            });
        };

        const rejectPendingWorkspace = () => {
            const disconnectError = new Error('Соединение с сервером потеряно');
            disconnectError.code = 'CATALOG_REVIEW_SOCKET_DISCONNECTED';

            const channel = workspaceChannelRef.current;
            [
                ...(channel.pendingWaiters || []),
                ...(channel.inFlightWaiters || []),
            ].forEach(({ reject }) => reject?.(disconnectError));

            channel.inFlight = false;
            channel.pendingPatch = null;
            channel.pendingWaiters = [];
            channel.inFlightWaiters = [];
        };

        const clearDraftChannels = () => {
            draftChannelsRef.current.forEach((channel) => {
                (channel.idleWaiters || []).forEach((resolve) => resolve());
            });
            draftChannelsRef.current.clear();
        };

        const clearOperationTimers = () => {
            operationTimersRef.current.forEach((timer) => window.clearTimeout(timer));
            operationTimersRef.current.clear();
            queuedOperationsRef.current.clear();
            operationSentAtRef.current.clear();
            operationStartedRef.current.clear();
        };

        const onDisconnect = () => {
            joinedRef.current = false;
            setConnected(false);
            rejectPendingWorkspace();
            clearDraftChannels();
            clearOperationTimers();
            setOperations({});
        };

        const onConnectError = (error) => {
            setConnected(false);
            console.warn(
                'catalogReview socket connection error:',
                error?.message || 'unknown'
            );
        };

        const onOld = (payload = {}) => {
            handlersRef.current.onOldUpdated?.(payload);
        };

        const onItem = (payload = {}) => {
            handlersRef.current.onItemUpdated?.(payload);
        };

        const onProject = (payload = {}) => {
            handlersRef.current.onProjectUpdated?.(payload);
        };

        const onSharedWorkspace = (payload = {}) => {
            const own = Boolean(
                payload.socketId
                && socket.id
                && payload.socketId === socket.id
            );

            applyWorkspace(payload.workspace, {
                remote: !own,
                own,
                authoritative: true,
                socketId: payload.socketId || '',
                user: payload.user || null,
                clientMutationId: payload.clientMutationId || null,
            });
        };

        const onOperation = (payload = {}) => {
            if (!payload.key) return;

            setOperations((previous) => {
                const next = { ...previous };

                if (payload.running !== true) {
                    delete next[payload.key];
                } else {
                    next[payload.key] = {
                        ...payload,
                        receivedAt: Date.now(),
                    };
                }

                return next;
            });
        };

        const onSharedDraftPatch = (payload = {}) => {
            if (!payload.socketId) return;

            const own = payload.socketId === socket.id;
            if (payload.workspace) {
                applyWorkspace(payload.workspace, {
                    remote: !own,
                    own,
                    authoritative: true,
                    source: 'draftPatch',
                    socketId: payload.socketId,
                });
            }

            if (!own) handlersRef.current.onDraftPatch?.(payload);
        };

        const onSharedDataChanged = (payload = {}) => {
            if (!payload.socketId || payload.socketId === socket.id) return;
            handlersRef.current.onDataChanged?.(payload);
        };

        socket.on('connect', join);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);
        socket.on('catalogReview:oldUpdated', onOld);
        socket.on('catalogReview:itemUpdated', onItem);
        socket.on('catalogReview:projectUpdated', onProject);
        socket.on('catalogReview:workspace', onSharedWorkspace);
        socket.on('catalogReview:operation', onOperation);
        socket.on('catalogReview:draftPatch', onSharedDraftPatch);
        socket.on('catalogReview:dataChanged', onSharedDataChanged);

        if (socket.connected) join();
        else socket.connect();

        const operationPruner = window.setInterval(() => {
            const timestamp = Date.now();

            setOperations((previous) => {
                let changed = false;
                const next = { ...previous };

                Object.entries(next).forEach(([key, operation]) => {
                    if (
                        timestamp - Number(operation.receivedAt || 0)
                        > OPERATION_TTL
                    ) {
                        delete next[key];
                        changed = true;
                    }
                });

                return changed ? next : previous;
            });
        }, 1500);

        return () => {
            window.clearInterval(operationPruner);
            clearOperationTimers();
            clearDraftChannels();

            socket.off('connect', join);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
            socket.off('catalogReview:oldUpdated', onOld);
            socket.off('catalogReview:itemUpdated', onItem);
            socket.off('catalogReview:projectUpdated', onProject);
            socket.off('catalogReview:workspace', onSharedWorkspace);
            socket.off('catalogReview:operation', onOperation);
            socket.off('catalogReview:draftPatch', onSharedDraftPatch);
            socket.off('catalogReview:dataChanged', onSharedDataChanged);

            if (socket.connected) {
                socket.emit('catalogReview:leave', tokenPayload());
            }

            joinedRef.current = false;
        };
    }, [
        applyOperations,
        applyWorkspace,
        enabled,
    ]);

    const updateOld = useCallback((payload) => emitWithAck(
        getSocket(),
        'catalogReview:updateOld',
        {
            ...payload,
            ...tokenPayload(),
            clientMutationId: mutationId(),
        }
    ), []);

    const updateItem = useCallback((payload) => emitWithAck(
        getSocket(),
        'catalogReview:updateItem',
        {
            ...payload,
            ...tokenPayload(),
            clientMutationId: mutationId(),
        }
    ), []);

    const transmitWorkspace = useCallback(() => {
        const channel = workspaceChannelRef.current;
        if (channel.inFlight || !channel.pendingPatch) return;

        const patch = channel.pendingPatch;
        const waiters = channel.pendingWaiters;
        channel.pendingPatch = null;
        channel.pendingWaiters = [];
        channel.inFlightWaiters = waiters;
        channel.inFlight = true;

        emitWithAck(
            getSocket(),
            'catalogReview:workspace',
            {
                ...tokenPayload(),
                patch,
                clientMutationId: mutationId(),
            }
        ).then((result) => {
            applyWorkspace(result.workspace, {
                own: true,
                authoritative: true,
                source: 'workspaceAck',
            });
            waiters.forEach(({ resolve }) => resolve(result));
        }).catch((error) => {
            waiters.forEach(({ reject }) => reject(error));
            resyncState().catch(() => {});
        }).finally(() => {
            channel.inFlight = false;
            channel.inFlightWaiters = [];
            if (channel.pendingPatch) transmitWorkspace();
        });
    }, [applyWorkspace, resyncState]);

    const updateWorkspace = useCallback((patch = {}) => new Promise((resolve, reject) => {
        if (!enabled) {
            resolve({
                ok: true,
                disabled: true,
                workspace: workspaceRef.current,
            });
            return;
        }

        const socket = getSocket();
        if (!socket.connected || !joinedRef.current) {
            const error = new Error('Нет соединения с сервером');
            error.code = 'CATALOG_REVIEW_SOCKET_OFFLINE';
            reject(error);
            return;
        }

        const channel = workspaceChannelRef.current;
        channel.pendingPatch = mergeWorkspacePatch(
            channel.pendingPatch || {},
            patch
        );
        channel.pendingWaiters.push({ resolve, reject });
        transmitWorkspace();
    }), [enabled, transmitWorkspace]);

    const notifyDataChanged = useCallback((payload = {}) => {
        if (!enabled) {
            return Promise.resolve({ ok: true, disabled: true });
        }

        return emitWithAck(
            getSocket(),
            'catalogReview:dataChanged',
            {
                ...payload,
                ...tokenPayload(),
                changeId: payload.changeId || mutationId(),
                refreshToken: payload.refreshToken || Date.now(),
            }
        );
    }, [enabled]);

    const flushOperation = useCallback((key) => {
        const timer = operationTimersRef.current.get(key);
        if (timer) window.clearTimeout(timer);
        operationTimersRef.current.delete(key);

        const operation = queuedOperationsRef.current.get(key);
        queuedOperationsRef.current.delete(key);
        if (!operation) return;

        const socket = getSocket();
        if (!socket.connected || !joinedRef.current) return;

        operationSentAtRef.current.set(key, Date.now());

        const first = !operationStartedRef.current.has(key);
        const terminal = operation.running !== true;

        if (operation.running) operationStartedRef.current.add(key);
        else operationStartedRef.current.delete(key);

        if (first || terminal) {
            socket.emit('catalogReview:operation', operation);
        } else {
            emitVolatile(socket, 'catalogReview:operation', operation);
        }
    }, []);

    const publishOperation = useCallback((payload = {}) => {
        const operation = {
            ...payload,
            key: String(payload.key || ''),
            running: payload.running === true,
        };

        if (!operation.key) return Promise.resolve(null);

        const socket = getSocket();
        setOperations((previous) => {
            const next = { ...previous };
            if (operation.running) {
                next[operation.key] = {
                    ...operation,
                    socketId: socket.id || '',
                    receivedAt: Date.now(),
                };
            } else {
                delete next[operation.key];
            }
            return next;
        });

        if (!enabled || !socket.connected || !joinedRef.current) {
            return Promise.resolve(operation);
        }

        queuedOperationsRef.current.set(operation.key, operation);

        if (!operation.running) {
            flushOperation(operation.key);
            return Promise.resolve(operation);
        }

        if (!operationTimersRef.current.has(operation.key)) {
            const elapsed = Date.now() - Number(
                operationSentAtRef.current.get(operation.key) || 0
            );
            const delay = Math.max(0, 140 - elapsed);

            if (delay === 0) {
                flushOperation(operation.key);
            } else {
                operationTimersRef.current.set(
                    operation.key,
                    window.setTimeout(
                        () => flushOperation(operation.key),
                        delay
                    )
                );
            }
        }

        return Promise.resolve(operation);
    }, [enabled, flushOperation]);

    const transmitDraft = useCallback((key) => {
        const channel = draftChannelsRef.current.get(key);
        if (!channel || channel.inFlight || !channel.pending) return;

        if (!getSocket().connected || !joinedRef.current) {
            channel.pending = null;
            (channel.idleWaiters || []).splice(0).forEach((resolve) => resolve());
            draftChannelsRef.current.set(key, channel);
            return;
        }

        const payload = channel.pending;
        channel.pending = null;
        channel.inFlight = true;
        draftChannelsRef.current.set(key, channel);

        emitWithAck(getSocket(), 'catalogReview:draftPatch', {
            ...tokenPayload(),
            ...payload,
        }).then((result) => {
            if (result.workspace) {
                applyWorkspace(result.workspace, {
                    own: true,
                    authoritative: true,
                    source: 'draftAck',
                });
            }
        }).catch((error) => {
            console.warn(
                'catalogReview draft sync error:',
                error.message || error.code || 'unknown'
            );
            resyncState().catch(() => {});
        }).finally(() => {
            const current = draftChannelsRef.current.get(key);
            if (!current) return;

            current.inFlight = false;
            draftChannelsRef.current.set(key, current);

            if (current.pending) {
                transmitDraft(key);
            } else if (current.idleWaiters?.length) {
                const waiters = current.idleWaiters.splice(0);
                waiters.forEach((resolve) => resolve());
            }
        });
    }, [applyWorkspace, resyncState]);

    const sendDraftPatch = useCallback(({
        scope,
        entityNumber = null,
        field,
        value,
    }) => {
        if (!enabled || !getSocket().connected || !joinedRef.current) return 0;

        const key = `${scope}:${entityNumber || 0}:${field}`;
        const previous = draftChannelsRef.current.get(key) || {
            sequence: 0,
            inFlight: false,
            pending: null,
            idleWaiters: [],
        };
        const sequence = previous.sequence + 1;

        draftChannelsRef.current.set(key, {
            ...previous,
            sequence,
            pending: {
                scope,
                entityNumber,
                field,
                value,
                sequence,
            },
        });

        transmitDraft(key);
        return sequence;
    }, [enabled, transmitDraft]);

    const flushDraftPatches = useCallback(() => {
        draftChannelsRef.current.forEach((_channel, key) => transmitDraft(key));
    }, [transmitDraft]);

    const operationList = useMemo(
        () => Object.values(operations),
        [operations]
    );

    return {
        collaborationEnabled: enabled,
        connected,
        workspace,
        operations,
        operationList,
        updateOld,
        updateItem,
        updateWorkspace,
        notifyDataChanged,
        publishOperation,
        sendDraftPatch,
        flushDraftPatches,
        resyncState,
    };
};
